import { FormEvent, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import API from "../../../services";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/store";
import "./WhitelabelSupportChatPopup.scss";

type WhitelabelSupportChatPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  ticketId: string;
};

type TicketUpdate = {
  Comment?: string;
  UpdatedBy?: string;
  UpdatedAt?: number;
  CreatedAt?: number;
};

type TicketDetails = {
  TicketId?: string;
  Status?: string;
  Description?: string;
  CreatedBy?: string;
  CreatedAt?: number;
  Updates?: TicketUpdate[];
};

type ChatMessage = {
  id: string;
  text: string;
  authorLabel: "You" | "Admin";
  createdAt: number;
  isUser: boolean;
};

const formatMessageTime = (time: number) =>
  new Date(time).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

const WhitelabelSupportChatPopup = ({
  isOpen,
  onClose,
  ticketId,
}: WhitelabelSupportChatPopupProps) => {
  const userName = useSelector((state: RootState) =>
    String(state.userDetails.userDetails?.UserName ?? "").trim()
  );
  const [ticketDetails, setTicketDetails] = useState<TicketDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messageDraft, setMessageDraft] = useState("");
  const listRef = useRef<HTMLDivElement | null>(null);

  const resolvedTicketId = String(ticketDetails?.TicketId ?? ticketId);

  const loadTicket = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await API.post("/webapp/get-ticket", { ticketId });
      const data = response?.data;
      if (data?.status !== "RS_OK" || !data?.ticket) {
        const msg = data?.errorDescription || "Unable to load support chat.";
        setError(msg);
        return;
      }
      setTicketDetails(data.ticket);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { errorDescription?: string } } })?.response?.data
          ?.errorDescription || "Unable to load support chat.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    if (!isOpen || !ticketId) return;
    void loadTicket();
  }, [isOpen, ticketId, loadTicket]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event as unknown as globalThis.KeyboardEvent).key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown as unknown as EventListener);
    return () => {
      window.removeEventListener("keydown", onKeyDown as unknown as EventListener);
    };
  }, [isOpen, onClose]);

  const messages = useMemo<ChatMessage[]>(() => {
    if (!ticketDetails) return [];
    const normalizedCurrentUser = userName.toLowerCase();
    const resolveAuthorLabel = (updatedBy: string): "You" | "Admin" => {
      const normalizedUpdatedBy = updatedBy.trim().toLowerCase();
      return normalizedUpdatedBy.length > 0 && normalizedUpdatedBy === normalizedCurrentUser
        ? "You"
        : "Admin";
    };
    const base: ChatMessage[] = [];
    const updates = Array.isArray(ticketDetails.Updates) ? ticketDetails.Updates : [];
    updates.forEach((item, index) => {
      const text = String(item?.Comment ?? "");
      if (!text) return;
      const updatedBy = String(item?.UpdatedBy ?? "");
      const authorLabel = resolveAuthorLabel(updatedBy);
      const timestamp = item?.UpdatedAt ?? item?.CreatedAt ?? Date.now();
      base.push({
        id: `update-${index}-${timestamp}`,
        text,
        authorLabel,
        createdAt: timestamp,
        isUser: authorLabel === "You",
      });
    });
    return [...base].sort((a, b) => a.createdAt - b.createdAt);
  }, [ticketDetails, userName]);

  useEffect(() => {
    if (!isOpen) return;
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, isOpen]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = messageDraft.trim();
    if (!trimmed || !resolvedTicketId || submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        comment: trimmed,
        status: String(ticketDetails?.Status ?? ""),
        ticketId: resolvedTicketId,
      };
      const formData = new FormData();
      formData.append("messageData", JSON.stringify(payload));
      const response = await API.post("/webapp/update-support-ticket", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      const data = response?.data;
      if (data?.status !== "RS_OK") {
        const msg = data?.errorDescription || "Unable to send message.";
        toast.error(msg);
        return;
      }
      setMessageDraft("");
      await loadTicket();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { errorDescription?: string } } })?.response?.data
          ?.errorDescription || "Unable to send message.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void handleSubmit(event as unknown as FormEvent);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="wl-support-chat__overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Customer support chat"
      onClick={onClose}
    >
      <div className="wl-support-chat__popup" onClick={(event) => event.stopPropagation()}>
        <header className="wl-support-chat__header">
          <div className="wl-support-chat__header-meta">
            <p className="wl-support-chat__title">Customer Support</p>
          </div>
          <button
            type="button"
            className="wl-support-chat__close"
            onClick={onClose}
            aria-label="Close support chat"
          >
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </button>
        </header>

        <div className="wl-support-chat__body" ref={listRef}>
          {loading ? <p className="wl-support-chat__status">Loading messages...</p> : null}
          {!loading && error ? <p className="wl-support-chat__status wl-support-chat__status--error">{error}</p> : null}
          {!loading && !error && messages.length === 0 ? (
            <p className="wl-support-chat__status">No updates yet. Start the conversation below.</p>
          ) : null}
          {!loading && !error
            ? messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`wl-support-chat__bubble-wrap ${
                    msg.isUser ? "wl-support-chat__bubble-wrap--user" : "wl-support-chat__bubble-wrap--support"
                  }`}
                >
                  <div className="wl-support-chat__bubble">
                    <p className="wl-support-chat__author">{msg.authorLabel}</p>
                    <p className="wl-support-chat__text">{msg.text}</p>
                    <p className="wl-support-chat__time">{formatMessageTime(msg.createdAt)}</p>
                  </div>
                </div>
              ))
            : null}
        </div>

        <form className="wl-support-chat__composer" onSubmit={handleSubmit}>
          <textarea
            rows={1}
            value={messageDraft}
            onChange={(event) => setMessageDraft(event.target.value)}
            onKeyDown={handleComposerKeyDown}
            className="wl-support-chat__input"
            placeholder="Type your message..."
            disabled={submitting || loading}
          />
          <button
            type="submit"
            className="wl-support-chat__send"
            disabled={submitting || loading || messageDraft.trim().length === 0}
            aria-label="Send message"
            title="Send"
          >
            <SendRoundedIcon sx={{ fontSize: 18 }} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default WhitelabelSupportChatPopup;
